// src/components/Toast.tsx
import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";

type ToastType = "success" | "error" | "warning" | "info";

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for exit animation
    }, 5000);

    const progressTimer = setInterval(() => {
      setProgress((prev) => Math.max(0, prev - 20)); // 5 seconds total
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(progressTimer);
    };
  }, [onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const getTypeStyles = () => {
    switch (type) {
      case "success":
        return {
          bg: "bg-green-50 border-green-200",
          text: "text-green-800",
          icon: <CheckCircle className="w-6 h-6 text-green-600" />,
          progress: "bg-green-500",
        };
      case "error":
        return {
          bg: "bg-red-100 border-red-200",
          text: "text-red-800",
          icon: <XCircle className="w-6 h-6 text-red-600" />,
          progress: "bg-red-500",
        };
      case "warning":
        return {
          bg: "bg-yellow-50 border-yellow-200",
          text: "text-yellow-800",
          icon: <AlertCircle className="w-6 h-6 text-yellow-600" />,
          progress: "bg-yellow-500",
        };
      case "info":
        return {
          bg: "bg-blue-50 border-blue-200",
          text: "text-blue-800",
          icon: <Info className="w-6 h-6 text-blue-600" />,
          progress: "bg-blue-500",
        };
      default:
        return {
          bg: "bg-gray-50 border-gray-200",
          text: "text-gray-800",
          icon: <Info className="w-5 h-5 text-gray-600" />,
          progress: "bg-gray-500",
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div
      className={`${styles.bg} border ${styles.text} rounded-lg shadow-lg p-5 min-w-[360px] max-w-lg transform transition-all duration-300 ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "-translate-y-2 opacity-0 pointer-events-none"
      }`}
      role="alert"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-0.5">{styles.icon}</div>
        <div className="ml-3 flex-1">
          <p className="text-base font-medium leading-relaxed">{message}</p>

        </div>
        <button
          onClick={handleClose}
          className="ml-4 flex-shrink-0 hover:bg-gray-100 rounded-full p-1 transition-colors"
          aria-label="Close toast"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Progress bar */}
      <div className="mt-3 h-1 w-full bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${styles.progress} transition-all duration-1000 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default Toast;
